/** 现金管理卡片：只接收密码解锁后的详情和保存回调，避免从全局业务状态直接展示管理信息。 */
import React, { useState } from "react";
import { Switch } from "./ui/switch";
import { toast } from "./ui/sonner";
import { Banknote, ShieldAlert } from "lucide-react";
import { fmtDT } from "../lib/date";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "./ui/alert-dialog";

/** zh 控制语言；cashMode 为受保护接口返回的详情；onChange 返回保存结果并同步业务启停状态。 */
export const CashModeCard = ({ zh, cashMode, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);
  if (!cashMode?.canToggle) return null;
  const on = !!cashMode.enabled;
  const toggle = async (next) => {
    // 现金开关影响所有收付与统计：使用项目确认弹窗，取消时绝不发送写请求。
    setPendingValue(null);
    setBusy(true);
    try {
      await onChange(next);
      toast.success(next ? (zh ? "现金功能已开启" : "Cash enabled") : (zh ? "现金功能已关闭，现金收付已全部屏蔽" : "Cash disabled and hidden"));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <>
    <div data-testid="cash-mode-card" data-enabled={on ? "1" : "0"}
      className={`rounded-xl border p-4 flex flex-wrap items-center gap-4 ${on ? "border-slate-200 bg-white" : "border-amber-300 bg-amber-50"}`}>
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-emerald-50 text-emerald-600" : "bg-amber-100 text-amber-700"}`}>
        {on ? <Banknote className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0 basis-56">
        <p className="text-sm font-semibold text-slate-900 flex flex-wrap items-center gap-2">
          {zh ? "现金功能" : "Cash"}
          <span data-testid="cash-mode-status" className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${on ? "bg-emerald-100 text-emerald-700" : "bg-amber-200 text-amber-900"}`}>
            {on ? (zh ? "已开启" : "ON") : (zh ? "已关闭 · 现金收付已屏蔽" : "OFF · cash hidden")}
          </span>
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          {zh ? "仅 IT 管理员 / CEO 可切换。关闭后：不能登记现金收款与现金出款；历史现金收入 / 付款从列表、统计、报表中屏蔽（可逆，不删数据）。" : "IT admin / CEO only. When off: no cash receipts or payouts can be recorded and all cash history is hidden from lists, stats and reports (reversible)."}
          {cashMode.updatedAt && <span data-testid="cash-mode-updated" className="ml-1 text-slate-400">· {zh ? "最近操作" : "Last"}: {cashMode.updatedBy || "-"} {fmtDT(cashMode.updatedAt)}</span>}
        </p>
      </div>
      <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
        <span>{on ? (zh ? "开启中" : "Enabled") : (zh ? "已关闭" : "Disabled")}</span>
        <Switch aria-label={zh ? "启用现金功能" : "Enable cash"} data-testid="cash-mode-switch" checked={on} disabled={busy} onCheckedChange={setPendingValue} />
      </label>
    </div>
    <AlertDialog open={pendingValue !== null} onOpenChange={(open) => { if (!open) setPendingValue(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{pendingValue ? (zh ? "确认开启现金功能？" : "Enable cash?") : (zh ? "确认关闭现金功能？" : "Disable cash?")}</AlertDialogTitle>
          <AlertDialogDescription>
            {pendingValue
              ? (zh ? "历史现金收付将恢复显示与统计，并允许登记新的现金收付款。" : "Historical cash records will be visible again and new cash receipts and payouts will be allowed.")
              : (zh ? "全系统将不能登记现金收款或出款；历史现金收付会从列表、统计和报表中屏蔽。数据不会删除，重新开启后恢复。" : "Cash receipts and payouts will be disabled. Historical cash records will be hidden from lists, statistics and reports. Data is preserved and can be restored by enabling cash again.")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="min-h-11" data-testid="cash-mode-cancel">{zh ? "取消" : "Cancel"}</AlertDialogCancel>
          <AlertDialogAction data-testid="cash-mode-confirm" className={`min-h-11 ${pendingValue === false ? "bg-none bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}`} onClick={() => toggle(pendingValue)}>
            {pendingValue ? (zh ? "确认开启" : "Enable") : (zh ? "确认关闭" : "Disable")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
